import { useState, useRef, useEffect } from 'react'
import { Fish, StopCircle, ChevronDown, Camera, FileText, ClipboardList, Upload, X, Loader2 } from 'lucide-react'
import type { FishDiagnosisRequest, InputMode, ReferencedHealthCheck } from '../types'
import { SYMPTOM_OPTIONS } from '../types'
import { useFishSpecies } from '../hooks'
import { imageApi } from '@/shared/api/imageApi'
import { fishHealthApi, type FishHealthCheck } from '@/api/fishHealthApi'
import { HealthCheckSelector } from './HealthCheckSelector'

interface FishDiagnosisFormProps {
  ponds: Array<{ id: string; name: string }>
  isStreaming: boolean
  onSubmit: (request: FishDiagnosisRequest) => void
  onCancel: () => void
  // New props for health check reference integration
  farmId?: string
  initialPondId?: string
  initialHealthCheckId?: string
}

const INPUT_MODE_TABS: Array<{ id: InputMode; label: string; icon: React.ReactNode; description: string }> = [
  {
    id: 'photo',
    label: 'Photo Only',
    icon: <Camera className="h-4 w-4" />,
    description: 'Upload fish photos for AI vision analysis'
  },
  {
    id: 'photo_symptoms',
    label: 'Photo + Symptoms',
    icon: <ClipboardList className="h-4 w-4" />,
    description: 'Photos combined with symptom checklist'
  },
  {
    id: 'text',
    label: 'Text Description',
    icon: <FileText className="h-4 w-4" />,
    description: 'Describe symptoms in your own words'
  },
]

export function FishDiagnosisForm({
  ponds,
  isStreaming,
  onSubmit,
  onCancel,
  farmId,
  initialPondId,
  initialHealthCheckId,
}: FishDiagnosisFormProps) {
  const [pondId, setPondId] = useState(initialPondId || '')
  const [isPondDropdownOpen, setIsPondDropdownOpen] = useState(false)
  const [inputMode, setInputMode] = useState<InputMode>('photo')
  const [symptoms, setSymptoms] = useState<string[]>([])
  const [description, setDescription] = useState('')
  const [fishSpecies, setFishSpecies] = useState('')
  const [affectedCount, setAffectedCount] = useState('')
  const [duration, setDuration] = useState('')
  const [selectedFiles, setSelectedFiles] = useState<File[]>([])
  const [filePreviews, setFilePreviews] = useState<string[]>([])
  const [isUploading, setIsUploading] = useState(false)
  const [uploadProgress, setUploadProgress] = useState<string>('')
  const [uploadError, setUploadError] = useState<string>('')

  // Referenced health checks state
  const [referencedChecks, setReferencedChecks] = useState<FishHealthCheck[]>([])
  const [isLoadingInitialCheck, setIsLoadingInitialCheck] = useState(false)

  const pondDropdownRef = useRef<HTMLDivElement>(null)
  const fileInputRef = useRef<HTMLInputElement>(null)
  const selectedPond = ponds.find(p => p.id === pondId)

  // Fetch fish species for dropdown
  const { data: speciesData } = useFishSpecies(true)
  const species = speciesData?.species || []

  // Close dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (pondDropdownRef.current && !pondDropdownRef.current.contains(event.target as Node)) {
        setIsPondDropdownOpen(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  // Cleanup file previews on unmount
  useEffect(() => {
    return () => {
      filePreviews.forEach(url => URL.revokeObjectURL(url))
    }
  }, [filePreviews])

  // Load initial health check when provided via URL params
  useEffect(() => {
    if (!initialHealthCheckId || !farmId || !initialPondId) return
    if (referencedChecks.some(c => c.id === initialHealthCheckId)) return // Already loaded

    const loadInitialCheck = async () => {
      setIsLoadingInitialCheck(true)
      try {
        const response = await fishHealthApi.listChecks(farmId, { pondId: initialPondId, limit: 100 })
        const check = response.data.find(c => c.id === initialHealthCheckId)
        if (check) {
          setReferencedChecks([check])
        }
      } catch (error) {
        console.error('Failed to load initial health check:', error)
      } finally {
        setIsLoadingInitialCheck(false)
      }
    }

    loadInitialCheck()
    // eslint-disable-next-line react-hooks/exhaustive-deps -- referencedChecks is intentionally excluded to prevent re-fetch loop
  }, [initialHealthCheckId, farmId, initialPondId])

  // Clear referenced checks when pond changes
  useEffect(() => {
    if (pondId !== initialPondId) {
      setReferencedChecks([])
    }
  }, [pondId, initialPondId])

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || [])
    if (files.length === 0) return

    // Add new files to existing selection (max 5 files)
    const newFiles = [...selectedFiles, ...files].slice(0, 5)
    setSelectedFiles(newFiles)

    // Create previews
    const newPreviews = newFiles.map(file => URL.createObjectURL(file))
    filePreviews.forEach(url => URL.revokeObjectURL(url))
    setFilePreviews(newPreviews)
  }

  const removeFile = (index: number) => {
    const newFiles = selectedFiles.filter((_, i) => i !== index)
    setSelectedFiles(newFiles)

    URL.revokeObjectURL(filePreviews[index])
    const newPreviews = filePreviews.filter((_, i) => i !== index)
    setFilePreviews(newPreviews)
  }

  const toggleSymptom = (symptom: string) => {
    setSymptoms(prev =>
      prev.includes(symptom)
        ? prev.filter(s => s !== symptom)
        : [...prev, symptom]
    )
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!pondId) return

    // Collect all imageIds from referenced checks
    const referencedImageIds = referencedChecks.flatMap(c => c.imageIds || [])
    const hasReferencedImages = referencedImageIds.length > 0

    // Validate based on input mode - allow submission if we have referenced check images
    if ((inputMode === 'photo' || inputMode === 'photo_symptoms') && selectedFiles.length === 0 && !hasReferencedImages) {
      return // Require at least one photo for photo modes (unless we have referenced check images)
    }
    if (inputMode === 'text' && !description.trim() && referencedChecks.length === 0) {
      return // Require description for text mode (unless we have referenced checks)
    }

    // Clear any previous errors
    setUploadError('')

    // Upload images to R2 first if in photo mode
    const uploadedImageIds: string[] = []
    if ((inputMode === 'photo' || inputMode === 'photo_symptoms') && selectedFiles.length > 0) {
      setIsUploading(true)
      setUploadProgress(`Uploading images (0/${selectedFiles.length})...`)

      try {
        for (let i = 0; i < selectedFiles.length; i++) {
          setUploadProgress(`Uploading images (${i + 1}/${selectedFiles.length})...`)
          const imageId = await imageApi.uploadFile(selectedFiles[i])
          uploadedImageIds.push(imageId)
        }
        setUploadProgress('')
      } catch (error) {
        console.error('Failed to upload images:', error)
        setUploadError(error instanceof Error ? error.message : 'Failed to upload images')
        setIsUploading(false)
        return
      } finally {
        setIsUploading(false)
      }
    }

    // Combine uploaded images with referenced check images
    const allImageIds = [...uploadedImageIds, ...referencedImageIds]

    // Build referenced health checks data for the request
    const referencedHealthChecks: ReferencedHealthCheck[] = referencedChecks.map(check => ({
      checkId: check.id,
      checkedAt: check.checkedAt,
      healthScore: check.healthScore,
      riskLevel: check.riskLevel,
      averageWeight: check.averageWeight,
      sampleSize: check.sampleSize,
      mortalityCount: check.mortalityCount,
      mortalityRate: check.mortalityRate,
      feedConversionRatio: check.feedConversionRatio,
      physicalSymptoms: check.physicalSymptoms,
      behaviorObservations: check.behaviorObservations,
      observations: check.observations,
      parasiteCode: check.parasiteCode,
      parasiteName: check.parasiteName,
      treatmentApplied: check.treatmentApplied,
      imageIds: check.imageIds,
    }))

    onSubmit({
      pondId,
      inputMode,
      imageIds: allImageIds.length > 0 ? allImageIds : undefined,
      symptoms: symptoms.length > 0 ? symptoms : undefined,
      description: description || undefined,
      fishSpecies: fishSpecies || undefined,
      affectedCount: affectedCount ? parseInt(affectedCount) : undefined,
      duration: duration || undefined,
      referencedHealthChecks: referencedHealthChecks.length > 0 ? referencedHealthChecks : undefined,
    })
  }

  const handlePondSelect = (id: string) => {
    setPondId(id)
    setIsPondDropdownOpen(false)
  }

  const isPhotoMode = inputMode === 'photo' || inputMode === 'photo_symptoms'
  const showSymptoms = inputMode === 'photo_symptoms' || inputMode === 'text'
  const referencedImageIds = referencedChecks.flatMap(c => c.imageIds || [])
  const hasReferencedImages = referencedImageIds.length > 0
  const canSubmit = pondId && !isUploading && !isLoadingInitialCheck && (
    (isPhotoMode && (selectedFiles.length > 0 || hasReferencedImages)) ||
    (inputMode === 'text' && (description.trim() || referencedChecks.length > 0))
  )
  const isDisabled = isStreaming || isUploading || isLoadingInitialCheck

  return (
    <form onSubmit={handleSubmit} className="space-y-5">
      {/* Input Mode Tabs */}
      <div>
        <label className="block text-sm font-medium text-slate-700 mb-2">
          Diagnosis Method
        </label>
        <div className="flex flex-wrap gap-2">
          {INPUT_MODE_TABS.map((tab) => (
            <button
              key={tab.id}
              type="button"
              onClick={() => !isDisabled && setInputMode(tab.id)}
              disabled={isDisabled}
              className={`
                flex items-center gap-2 rounded-lg px-4 py-2.5 text-sm font-medium transition-all
                ${inputMode === tab.id
                  ? 'bg-emerald-600 text-white shadow-sm'
                  : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
                }
                ${isDisabled ? 'cursor-not-allowed opacity-50' : 'cursor-pointer'}
              `}
            >
              {tab.icon}
              {tab.label}
            </button>
          ))}
        </div>
        <p className="mt-2 text-xs text-slate-500">
          {INPUT_MODE_TABS.find(t => t.id === inputMode)?.description}
        </p>
      </div>

      {/* Pond Selection - Custom Dropdown */}
      <div>
        <label className="block text-sm font-medium text-slate-700 mb-2">
          Select Pond *
        </label>
        <div className="relative" ref={pondDropdownRef}>
          <button
            type="button"
            onClick={() => !isDisabled && setIsPondDropdownOpen(!isPondDropdownOpen)}
            disabled={isDisabled}
            className={`
              relative w-full rounded-lg border border-slate-200/50 bg-white px-4 py-2.5 text-left shadow-sm
              transition-all hover:border-primary-300 focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-500/20
              ${isDisabled ? 'cursor-not-allowed opacity-50' : 'cursor-pointer'}
            `}
          >
            <div className="flex items-center justify-between">
              <span className={`text-sm ${selectedPond ? 'text-gray-900' : 'text-gray-400'}`}>
                {selectedPond ? selectedPond.name : 'Choose a pond...'}
              </span>
              <ChevronDown
                className={`h-5 w-5 text-gray-400 transition-transform ${isPondDropdownOpen ? 'rotate-180' : ''}`}
              />
            </div>
          </button>

          {/* Dropdown Menu */}
          {isPondDropdownOpen && ponds.length > 0 && (
            <div className="absolute z-50 mt-2 w-full rounded-lg border border-slate-200/50 bg-white shadow-lg">
              <div className="max-h-60 overflow-auto py-1">
                {ponds.map((pond) => {
                  const isSelected = pondId === pond.id
                  return (
                    <button
                      key={pond.id}
                      type="button"
                      onClick={() => handlePondSelect(pond.id)}
                      className={`
                        flex w-full items-center gap-3 px-4 py-2.5 text-sm transition-colors
                        ${isSelected ? 'bg-primary-50 text-primary-700' : 'text-gray-700 hover:bg-gray-50'}
                      `}
                    >
                      <div
                        className={`h-4 w-4 rounded border-2 flex items-center justify-center ${
                          isSelected ? 'border-primary-500 bg-primary-500' : 'border-gray-300'
                        }`}
                      >
                        {isSelected && (
                          <svg className="h-3 w-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                          </svg>
                        )}
                      </div>
                      <span className="flex-1 text-left">{pond.name}</span>
                    </button>
                  )
                })}
              </div>
            </div>
          )}

          {/* Empty state when no ponds */}
          {isPondDropdownOpen && ponds.length === 0 && (
            <div className="absolute z-50 mt-2 w-full rounded-lg border border-slate-200/50 bg-white shadow-lg p-4">
              <p className="text-sm text-gray-500 text-center">No ponds available</p>
            </div>
          )}
        </div>
      </div>

      {/* Health Check References */}
      {farmId && (
        <HealthCheckSelector
          farmId={farmId}
          pondId={pondId}
          pondName={selectedPond?.name}
          selectedChecks={referencedChecks}
          onChecksChange={setReferencedChecks}
          maxChecks={3}
          disabled={isDisabled}
        />
      )}

      {/* Loading state for initial health check */}
      {isLoadingInitialCheck && (
        <div className="flex items-center gap-2 p-3 bg-blue-50 border border-blue-200/50 rounded-lg text-sm text-blue-700">
          <Loader2 className="h-4 w-4 animate-spin" />
          Loading health check data...
        </div>
      )}

      {/* Photo Upload (for photo modes) */}
      {isPhotoMode && (
        <div>
          <label className="block text-sm font-medium text-slate-700 mb-2">
            Upload Photos {hasReferencedImages ? '(optional)' : '*'}
          </label>
          <div className="space-y-3">
            {/* Upload area */}
            <div
              onClick={() => !isDisabled && fileInputRef.current?.click()}
              className={`
                border-2 border-dashed border-slate-300 rounded-lg p-6 text-center transition-colors
                ${isDisabled ? 'cursor-not-allowed opacity-50' : 'cursor-pointer hover:border-emerald-400 hover:bg-emerald-50/50'}
              `}
            >
              <Upload className="mx-auto h-8 w-8 text-slate-400" />
              <p className="mt-2 text-sm text-slate-600">
                Click to upload fish photos
              </p>
              <p className="mt-1 text-xs text-slate-400">
                PNG, JPG up to 10MB each (max 5 photos)
              </p>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                multiple
                onChange={handleFileChange}
                className="hidden"
                disabled={isDisabled}
              />
            </div>

            {/* File previews */}
            {filePreviews.length > 0 && (
              <div className="grid grid-cols-5 gap-2">
                {filePreviews.map((preview, index) => (
                  <div key={index} className="relative group">
                    <img
                      src={preview}
                      alt={`Preview ${index + 1}`}
                      className="w-full h-16 object-cover rounded-lg border border-slate-200"
                    />
                    <button
                      type="button"
                      onClick={() => removeFile(index)}
                      disabled={isDisabled}
                      className="absolute -top-2 -right-2 p-1 bg-red-500 text-white rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
                    >
                      <X className="h-3 w-3" />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Symptoms Checklist */}
      {showSymptoms && (
        <div>
          <label className="block text-sm font-medium text-slate-700 mb-2">
            Observed Symptoms {inputMode === 'photo_symptoms' ? '' : '*'}
          </label>
          <div className="grid grid-cols-2 gap-2">
            {SYMPTOM_OPTIONS.map((symptom) => {
              const isSelected = symptoms.includes(symptom)
              return (
                <button
                  key={symptom}
                  type="button"
                  onClick={() => !isDisabled && toggleSymptom(symptom)}
                  disabled={isDisabled}
                  className={`
                    flex items-center gap-2 rounded-lg px-3 py-2 text-sm text-left transition-colors
                    ${isSelected
                      ? 'bg-emerald-100 text-emerald-800 border border-emerald-300'
                      : 'bg-slate-50 text-slate-700 border border-slate-200/50 hover:bg-slate-100'
                    }
                    ${isDisabled ? 'cursor-not-allowed opacity-50' : 'cursor-pointer'}
                  `}
                >
                  <div
                    className={`h-4 w-4 rounded border flex-shrink-0 flex items-center justify-center ${
                      isSelected ? 'border-emerald-500 bg-emerald-500' : 'border-gray-300'
                    }`}
                  >
                    {isSelected && (
                      <svg className="h-3 w-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                      </svg>
                    )}
                  </div>
                  <span className="truncate">{symptom}</span>
                </button>
              )
            })}
          </div>
        </div>
      )}

      {/* Description */}
      <div>
        <label className="block text-sm font-medium text-slate-700 mb-2">
          {inputMode === 'text' ? 'Describe the Symptoms *' : 'Additional Details'}
        </label>
        <textarea
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          rows={3}
          placeholder={
            inputMode === 'text'
              ? "Describe what you're observing: fish behavior, appearance, affected areas..."
              : "Any additional context about the condition..."
          }
          className="w-full rounded-lg border border-slate-200/50 bg-white px-4 py-2.5 text-sm shadow-sm transition-all hover:border-primary-300 focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-500/20"
          disabled={isDisabled}
        />
      </div>

      {/* Optional Fields */}
      <div className="grid gap-4 sm:grid-cols-3">
        {/* Fish Species */}
        <div>
          <label className="block text-sm font-medium text-slate-700 mb-2">
            Fish Species
          </label>
          <select
            value={fishSpecies}
            onChange={(e) => setFishSpecies(e.target.value)}
            className="w-full rounded-lg border border-slate-200/50 bg-white px-4 py-2.5 text-sm shadow-sm transition-all hover:border-primary-300 focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-500/20"
            disabled={isDisabled}
          >
            <option value="">Select species...</option>
            {species.map((s) => (
              <option key={s.code} value={s.code}>
                {s.commonName}
              </option>
            ))}
          </select>
        </div>

        {/* Affected Count */}
        <div>
          <label className="block text-sm font-medium text-slate-700 mb-2">
            Affected Count
          </label>
          <input
            type="number"
            min={1}
            value={affectedCount}
            onChange={(e) => setAffectedCount(e.target.value)}
            placeholder="e.g., 50"
            className="w-full rounded-lg border border-slate-200/50 bg-white px-4 py-2.5 text-sm shadow-sm transition-all hover:border-primary-300 focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-500/20"
            disabled={isDisabled}
          />
        </div>

        {/* Duration */}
        <div>
          <label className="block text-sm font-medium text-slate-700 mb-2">
            Duration
          </label>
          <input
            type="text"
            value={duration}
            onChange={(e) => setDuration(e.target.value)}
            placeholder="e.g., 3 days"
            className="w-full rounded-lg border border-slate-200/50 bg-white px-4 py-2.5 text-sm shadow-sm transition-all hover:border-primary-300 focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-500/20"
            disabled={isDisabled}
          />
        </div>
      </div>

      {/* Upload Error */}
      {uploadError && (
        <div className="rounded-lg bg-red-50 border border-red-200 p-3 text-sm text-red-700">
          <strong>Upload failed:</strong> {uploadError}
        </div>
      )}

      {/* Submit Button */}
      <div className="flex items-center gap-3 pt-2">
        {isStreaming ? (
          <button
            type="button"
            onClick={onCancel}
            className="flex items-center gap-2 rounded-lg bg-red-600 px-4 py-2.5 text-sm font-medium text-white shadow-sm hover:bg-red-700 transition-colors"
          >
            <StopCircle className="h-4 w-4" />
            Stop Diagnosis
          </button>
        ) : isUploading ? (
          <button
            type="button"
            disabled
            className="flex items-center gap-2 rounded-lg bg-emerald-600 px-4 py-2.5 text-sm font-medium text-white shadow-sm opacity-75 cursor-not-allowed"
          >
            <Loader2 className="h-4 w-4 animate-spin" />
            {uploadProgress}
          </button>
        ) : (
          <button
            type="submit"
            disabled={!canSubmit}
            className="flex items-center gap-2 rounded-lg bg-emerald-600 px-4 py-2.5 text-sm font-medium text-white shadow-sm hover:bg-emerald-700 disabled:cursor-not-allowed disabled:opacity-50 transition-colors"
          >
            <Fish className="h-4 w-4" />
            Diagnose Fish Health
          </button>
        )}
      </div>
    </form>
  )
}

export default FishDiagnosisForm
